import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  confirmationRows,
  DEFAULT_FINANCING_PROGRAMS,
  financedTotal,
  findProgram,
  isFinancedProgram,
  type ContractDecisions,
  type FinancingProgram,
} from '@/lib/contract-decisions';

export type DecisionPatch = Partial<ContractDecisions> & { cardLast4?: string };

type Props = {
  cashTotal: number;
  programs: FinancingProgram[];
  decisions: ContractDecisions;
  disabled?: boolean;
  pending?: boolean;
  /** Choice C: tile + boxes, or down payment after the calc recap. */
  section?: 'choice' | 'downpay' | 'all';
  onSave: (patch: DecisionPatch) => Promise<void> | void;
};

function money(value: number) {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function ContractDecisionsPanel({
  cashTotal,
  programs,
  decisions,
  disabled,
  pending,
  section = 'all',
  onSave,
}: Props) {
  const catalog = programs.length ? programs : DEFAULT_FINANCING_PROGRAMS;
  const selected = findProgram(catalog, decisions.financingProgramId);
  const [applyOpen, setApplyOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [applyDraft, setApplyDraft] = useState({
    name: '',
    email: '',
    phone: '',
    income: '',
  });
  const [cardLast4, setCardLast4] = useState('');
  const [amountDraft, setAmountDraft] = useState(
    decisions.downPaymentAmount != null ? String(decisions.downPaymentAmount) : '',
  );

  const boxes = useMemo(
    () =>
      catalog.slice(0, 3).map((program) => ({
        ...program,
        amount: program.amount ?? financedTotal(cashTotal, program.rateFactor ?? 1),
      })),
    [catalog, cashTotal],
  );

  const confirmations = confirmationRows(decisions);

  const showChoice = section === 'choice' || section === 'all';
  const showDown = section === 'downpay' || section === 'all';
  const selectedTotal = selected
    ? financedTotal(cashTotal, selected.rateFactor ?? 1)
    : cashTotal;

  return (
    <div className="cd-decisions" data-testid={section === 'downpay' ? 'contract-decisions-downpay' : 'contract-decisions'}>
      {showChoice ? (
        <>
      <div className="cd-choice-tile" data-testid="client-choice-tile">
        <span className="cd-choice-when">Writes the contract</span>
        <h3>Client’s choice at signing</h3>
        <span className="cd-choice-amt-hero">{selected ? money(selectedTotal) : money(cashTotal)}</span>
        <span className="cd-choice-meta">
          {selected
            ? isFinancedProgram(selected)
              ? `Financed · total cost ${money(selectedTotal)}`
              : selected.name
            : disabled
              ? 'Locked after a client signed'
              : 'Pick a program'}
        </span>
      </div>

      <div className="cd-island" data-testid="contract-financing">
        <p className="cd-island-need">
          {disabled ? 'Locked' : selected ? 'On the contract' : 'Needs a decision'}
        </p>
        <p className="cd-muted">
          {disabled
            ? 'Financing is locked after a client signed. The total cannot change.'
            : 'Pick one program. Client’s choice at signing updates automatically.'}
        </p>
        {disabled ? (
          selected ? (
            <div className="cd-choice-row">
              <div
                className="cd-choice is-selected is-locked"
                data-testid="contract-financing-frozen"
              >
                <span className="cd-choice-kind">{selected.name}</span>
                <span className="cd-choice-amt">{money(selectedTotal)}</span>
                <span className="cd-choice-note">{selected.description}</span>
              </div>
            </div>
          ) : (
            <p className="cd-choice-note" data-testid="contract-financing-frozen">
              No program on this contract.
            </p>
          )
        ) : (
          <div className="cd-choice-row">
            {boxes.map((program) => (
              <button
                key={program.id}
                type="button"
                className={`cd-choice${selected?.id === program.id ? ' is-selected' : ''}`}
                aria-pressed={selected?.id === program.id}
                onClick={() => void onSave({ financingProgramId: program.id })}
              >
                <span className="cd-choice-kind">{program.name}</span>
                <span className="cd-choice-amt">{money(program.amount)}</span>
                <span className="cd-choice-note">{program.description}</span>
              </button>
            ))}
          </div>
        )}
        {!disabled && isFinancedProgram(selected) ? (
          <p className="cd-apply-row">
            <button
              type="button"
              className="cd-link"
              onClick={() => setApplyOpen(true)}
            >
              Apply for Financing
            </button>
            {decisions.financingConfirmation ? (
              <span> Confirmation {decisions.financingConfirmation}</span>
            ) : null}
          </p>
        ) : disabled && decisions.financingConfirmation ? (
          <p className="cd-apply-row">
            Confirmation {decisions.financingConfirmation}
          </p>
        ) : null}
      </div>
        </>
      ) : null}

      {showDown ? (
        <>
      <div className="cd-island" data-testid="contract-down-decision">
        <p className="cd-island-need">
          {disabled ? 'Locked' : decisions.downPaymentKind ? 'On the contract' : 'Needs a decision'}
        </p>
        <p className="cd-muted">
          {disabled
            ? 'Down payment is locked after a client signed.'
            : 'Down payment is separate from the financing program.'}
        </p>
        {disabled ? (
          <p className="cd-choice-note" data-testid="contract-down-frozen">
            {decisions.downPaymentKind === 'nothing'
              ? 'Nothing down'
              : decisions.downPaymentKind === 'amount' && decisions.downPaymentAmount
                ? money(decisions.downPaymentAmount)
                : 'No down-payment decision on this contract.'}
          </p>
        ) : (
        <div className="cd-choice-row is-two">
          <button
            type="button"
            className={`cd-choice${decisions.downPaymentKind === 'nothing' ? ' is-selected' : ''}`}
            onClick={() => void onSave({ downPaymentKind: 'nothing' })}
          >
            <span className="cd-choice-kind">Nothing Down</span>
            <span className="cd-choice-note">No amount due at signing</span>
          </button>
          <button
            type="button"
            className={`cd-choice${decisions.downPaymentKind === 'amount' ? ' is-selected' : ''}`}
            onClick={() =>
              void onSave({
                downPaymentKind: 'amount',
                downPaymentAmount: Number(amountDraft) || decisions.downPaymentAmount || 0,
              })
            }
          >
            <span className="cd-choice-kind">Enter an amount</span>
            <span className="cd-choice-note">Then type of payment</span>
          </button>
        </div>
        )}
        {!disabled && decisions.downPaymentKind === 'amount' ? (
          <>
            <div className="cd-field">
              <Label htmlFor="decision-down-amount">Down payment amount</Label>
              <Input
                id="decision-down-amount"
                type="number"
                min={1}
                step="0.01"
                value={amountDraft}
                disabled={disabled}
                onChange={(event) => setAmountDraft(event.target.value)}
                onBlur={() => {
                  const amount = Number(amountDraft);
                  if (amount > 0) void onSave({ downPaymentKind: 'amount', downPaymentAmount: amount });
                }}
              />
            </div>
            <div className="cd-choice-row is-four">
              {[
                ['check', 'Check'],
                ['credit_card', 'Credit card'],
                ['ach', 'ACH'],
                ['cash', 'Cash'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`cd-choice${decisions.downPaymentMethod === value ? ' is-selected' : ''}`}
                  disabled={disabled}
                  onClick={() => {
                    void onSave({ downPaymentMethod: value });
                    if (value === 'credit_card') setCardOpen(true);
                  }}
                >
                  <span className="cd-choice-kind">{label}</span>
                </button>
              ))}
            </div>
            {decisions.downPaymentMethod === 'check' ? (
              <div className="cd-field">
                <Label htmlFor="decision-check">Check number</Label>
                <Input
                  id="decision-check"
                  defaultValue={decisions.checkNumber ?? ''}
                  disabled={disabled}
                  placeholder="Required if paying by check"
                  onBlur={(event) => void onSave({ checkNumber: event.target.value })}
                />
              </div>
            ) : null}
            {decisions.downPaymentMethod === 'credit_card' ? (
              <p className="cd-apply-row">
                <button type="button" className="cd-link" disabled={disabled} onClick={() => setCardOpen(true)}>
                  Pay by card
                </button>
                {decisions.cardConfirmation ? <span> Confirmation {decisions.cardConfirmation}</span> : null}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      {confirmations.length > 0 ? (
        <div data-testid="contract-confirmations">
          <p className="cd-sub-h">Confirmations on this contract</p>
          <table className="cd-bar">
            <tbody>
              <tr className="cd-bar-head">
                <td colSpan={2}>Stored on the paper</td>
              </tr>
              {confirmations.map(([label, value]) => (
                <tr key={label}>
                  <td className="cd-lbl">{label}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
        </>
      ) : null}

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for Financing</DialogTitle>
            <DialogDescription>
              A confirmation number is stored on this contract when the application is submitted.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="apply-name">Full name</Label>
              <Input id="apply-name" value={applyDraft.name} onChange={(e) => setApplyDraft((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="apply-email">Email</Label>
              <Input id="apply-email" value={applyDraft.email} onChange={(e) => setApplyDraft((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="apply-phone">Phone</Label>
              <Input id="apply-phone" value={applyDraft.phone} onChange={(e) => setApplyDraft((p) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="apply-income">Annual income</Label>
              <Input id="apply-income" value={applyDraft.income} onChange={(e) => setApplyDraft((p) => ({ ...p, income: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button
              type="button"
              disabled={pending || !applyDraft.name.trim()}
              onClick={async () => {
                await onSave({ financingApplication: applyDraft });
                setApplyOpen(false);
              }}
            >
              Submit application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cardOpen} onOpenChange={setCardOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pay by card</DialogTitle>
            <DialogDescription>
              Capture the processor confirmation on this contract. This is the down payment, not the financing program.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="card-last4">Card last four</Label>
            <Input id="card-last4" inputMode="numeric" maxLength={4} value={cardLast4} onChange={(e) => setCardLast4(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCardOpen(false)}>Cancel</Button>
            <Button
              type="button"
              disabled={pending || cardLast4.replace(/\D/g, '').length < 4}
              onClick={async () => {
                await onSave({ cardLast4: cardLast4.replace(/\D/g, '').slice(-4) });
                setCardOpen(false);
              }}
            >
              Record confirmation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
